import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'components/Button';
import { productTypeService, specConstraintService } from '../../../api';
import type { ProductTypeViewModel } from 'api/types/product-type';
import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import { SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';
import type {
  ProductTypeSpecRule,
  SpecRuleConditionType,
  SpecRuleOperator,
} from 'api/types/product-type/ProductTypeSpecRule';
import { normalizeSpecConstraintList, normalizeSpecValueType } from 'utils/specConstraintUtils';
import { readFriendlyApiErrorMessage } from 'utils/apiErrorMessages';

export interface ProductTypeRuleModalProps {
  show: boolean;
  onClose: () => void;
  productTypes: ProductTypeViewModel[];
  initialProductTypeId?: number | null;
}

type TabKey = 'rules' | 'ordering';

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `r_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function operatorsForValueType(valueType: SpecValueType): { value: SpecRuleOperator; label: string }[] {
  if (valueType === SpecValueType.Number) {
    return [
      { value: '=', label: '= (eşittir)' },
      { value: '>', label: '> (büyüktür)' },
      { value: '<', label: '< (küçüktür)' },
      { value: '>=', label: '≥ (büyük eşittir)' },
      { value: '<=', label: '≤ (küçük eşittir)' },
    ];
  }
  if (valueType === SpecValueType.Enum) {
    return [
      { value: '=', label: 'eşittir' },
      { value: 'in', label: 'şunlardan biri' },
      { value: '!=', label: 'eşit değildir' },
    ];
  }
  return [
    { value: '=', label: 'eşittir' },
    { value: '!=', label: 'eşit değildir' },
  ];
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

/** Value editor for one side (condition or outcome) of a rule. */
function RuleValueEditor({
  constraint,
  operator,
  value,
  values,
  onChangeValue,
  onChangeValues,
}: {
  constraint: SpecConstraintViewModel | undefined;
  operator: SpecRuleOperator;
  value: string;
  values: string[];
  onChangeValue: (v: string) => void;
  onChangeValues: (v: string[]) => void;
}) {
  if (!constraint) return null;
  const valueType = normalizeSpecValueType(constraint.valueType);
  const allowed = (constraint.allowedValues ?? []).map((av) => av.value);

  if (valueType === SpecValueType.Enum) {
    if (operator === 'in') {
      return (
        <div className="d-flex flex-wrap gap-2">
          {allowed.map((v) => {
            const checked = values.includes(v);
            return (
              <label key={v} className="form-check form-check-inline m-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={checked}
                  onChange={() =>
                    onChangeValues(checked ? values.filter((x) => x !== v) : [...values, v])
                  }
                />
                <span className="form-check-label ms-1">{v}</span>
              </label>
            );
          })}
          {allowed.length === 0 && <span className="text-muted small">Seçenek yok</span>}
        </div>
      );
    }
    return (
      <select
        className="form-select form-select-sm"
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
      >
        <option value="">Seçin…</option>
        {allowed.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  }

  if (valueType === SpecValueType.Number) {
    return (
      <input
        type="number"
        className="form-control form-control-sm"
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        placeholder="Değer"
      />
    );
  }

  return (
    <input
      type="text"
      className="form-control form-control-sm"
      value={value}
      onChange={(e) => onChangeValue(e.target.value)}
      placeholder="Değer"
    />
  );
}

export default function ProductTypeRuleModal({
  show,
  onClose,
  productTypes,
  initialProductTypeId = null,
}: ProductTypeRuleModalProps) {
  const [tab, setTab] = useState<TabKey>('rules');
  const [selectedId, setSelectedId] = useState<number | null>(initialProductTypeId);
  const [constraints, setConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [rules, setRules] = useState<ProductTypeSpecRule[]>([]);
  const [orders, setOrders] = useState<Record<number, string[]>>({});
  const initialOrdersRef = useRef<Record<number, string[]>>({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setSelectedId(initialProductTypeId ?? null);
      setTab('rules');
      setError(null);
      setSuccess(null);
    }
  }, [show, initialProductTypeId]);

  useEffect(() => {
    if (!show || selectedId == null) {
      setConstraints([]);
      setRules([]);
      setOrders({});
      initialOrdersRef.current = {};
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSuccess(null);
    Promise.all([
      productTypeService.getSpecConstraints(selectedId),
      productTypeService.getRules(selectedId),
    ])
      .then(([constraintsRes, rulesRes]) => {
        if (cancelled) return;
        const list = constraintsRes.isSuccess ? normalizeSpecConstraintList(constraintsRes.data) : [];
        setConstraints(list);
        const orderMap: Record<number, string[]> = {};
        list.forEach((c) => {
          if (normalizeSpecValueType(c.valueType) === SpecValueType.Enum) {
            orderMap[c.id] = (c.allowedValues ?? []).map((av) => av.value);
          }
        });
        setOrders(orderMap);
        initialOrdersRef.current = JSON.parse(JSON.stringify(orderMap));
        setRules(rulesRes.isSuccess && rulesRes.data ? rulesRes.data : []);
      })
      .catch((e) => {
        if (!cancelled) setError(readFriendlyApiErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, selectedId]);

  const constraintByKey = useMemo(() => {
    const map = new Map<string, SpecConstraintViewModel>();
    constraints.forEach((c) => map.set(c.key, c));
    return map;
  }, [constraints]);

  const enumConstraints = useMemo(
    () => constraints.filter((c) => normalizeSpecValueType(c.valueType) === SpecValueType.Enum),
    [constraints]
  );

  if (!show) return null;

  const updateRule = (id: string, updater: (rule: ProductTypeSpecRule) => ProductTypeSpecRule) => {
    setRules((prev) => prev.map((r) => (r.id === id ? updater(r) : r)));
  };

  const addRule = () => {
    const firstSpec = constraints[0]?.key ?? '';
    setRules((prev) => [
      ...prev,
      {
        id: genId(),
        condition: { type: 'spec', specKey: firstSpec, operator: '=', value: '', values: [] },
        outcome: { specKey: firstSpec, operator: 'in', value: '', values: [] },
      },
    ]);
  };

  const removeRule = (id: string) => setRules((prev) => prev.filter((r) => r.id !== id));

  const reorder = (specId: number, from: number, to: number) => {
    setOrders((prev) => {
      const current = [...(prev[specId] ?? [])];
      if (from < 0 || to < 0 || from >= current.length || to >= current.length) return prev;
      const [moved] = current.splice(from, 1);
      current.splice(to, 0, moved);
      return { ...prev, [specId]: current };
    });
  };

  const handleSave = async () => {
    if (selectedId == null) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const invalid = rules.find((r) => !r.outcome.specKey);
      if (invalid) {
        setError('Her kuralın bir hedef özelliği olmalıdır.');
        setSaving(false);
        return;
      }

      await productTypeService.updateRules(selectedId, rules);

      for (const c of enumConstraints) {
        const order = orders[c.id] ?? [];
        const initial = initialOrdersRef.current[c.id] ?? [];
        if (order.length > 0 && !arraysEqual(order, initial)) {
          await specConstraintService.update(c.id, { allowedValues: order });
        }
      }

      initialOrdersRef.current = JSON.parse(JSON.stringify(orders));
      setSuccess('Değişiklikler kaydedildi.');
    } catch (e) {
      setError(readFriendlyApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} aria-modal="true" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-cog me-2" aria-hidden="true" />
                Özellik Kuralları
              </h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Kapat" />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label small fw-semibold">Ürün Tipi</label>
                <select
                  className="form-select"
                  value={selectedId ?? ''}
                  onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Ürün tipi seçin…</option>
                  {productTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {pt.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedId == null ? (
                <p className="text-muted small mb-0">Kuralları yönetmek için bir ürün tipi seçin.</p>
              ) : loading ? (
                <p className="text-muted small mb-0" role="status">
                  Yükleniyor…
                </p>
              ) : (
                <>
                  <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${tab === 'rules' ? 'active' : ''}`}
                        onClick={() => setTab('rules')}
                      >
                        Kurallar
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${tab === 'ordering' ? 'active' : ''}`}
                        onClick={() => setTab('ordering')}
                      >
                        Seçenek Sıralaması
                      </button>
                    </li>
                  </ul>

                  {constraints.length === 0 && (
                    <div className="alert alert-info py-2 small">
                      Bu ürün tipine bağlı özellik bulunamadı. Önce özellik (Özellik) tanımlayın.
                    </div>
                  )}

                  {tab === 'rules' && constraints.length > 0 && (
                    <div className="d-flex flex-column gap-3">
                      {rules.length === 0 && (
                        <p className="text-muted small mb-0">Henüz kural yok. "Kural Ekle" ile başlayın.</p>
                      )}
                      {rules.map((rule) => {
                        const isProductType = rule.condition.type === 'productType';
                        const condConstraint = isProductType
                          ? undefined
                          : constraintByKey.get(rule.condition.specKey ?? '');
                        const outcomeConstraint = constraintByKey.get(rule.outcome.specKey);
                        const condOps = condConstraint
                          ? operatorsForValueType(normalizeSpecValueType(condConstraint.valueType))
                          : [];
                        const outcomeOps = outcomeConstraint
                          ? operatorsForValueType(normalizeSpecValueType(outcomeConstraint.valueType))
                          : [];

                        return (
                          <div key={rule.id} className="border rounded p-3 position-relative">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-2"
                              onClick={() => removeRule(rule.id)}
                              aria-label="Kuralı sil"
                              title="Kuralı sil"
                            >
                              <i className="fas fa-trash" />
                            </button>

                            <div className="mb-2 small fw-semibold text-primary">EĞER</div>
                            <div className="row g-2 align-items-center mb-3">
                              <div className="col-12 col-sm-4">
                                <select
                                  className="form-select form-select-sm"
                                  value={rule.condition.type}
                                  onChange={(e) => {
                                    const type = e.target.value as SpecRuleConditionType;
                                    updateRule(rule.id, (r) => ({
                                      ...r,
                                      condition:
                                        type === 'productType'
                                          ? { type, specKey: null, operator: '=', value: '', values: [] }
                                          : {
                                              type,
                                              specKey: constraints[0]?.key ?? '',
                                              operator: '=',
                                              value: '',
                                              values: [],
                                            },
                                    }));
                                  }}
                                >
                                  <option value="spec">Özellik değeri</option>
                                  <option value="productType">Bu ürün tipi (her zaman)</option>
                                </select>
                              </div>
                              {!isProductType && (
                                <>
                                  <div className="col-12 col-sm-4">
                                    <select
                                      className="form-select form-select-sm"
                                      value={rule.condition.specKey ?? ''}
                                      onChange={(e) =>
                                        updateRule(rule.id, (r) => ({
                                          ...r,
                                          condition: { ...r.condition, specKey: e.target.value, value: '', values: [] },
                                        }))
                                      }
                                    >
                                      {constraints.map((c) => (
                                        <option key={c.id} value={c.key}>
                                          {c.displayName}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="col-12 col-sm-4">
                                    <select
                                      className="form-select form-select-sm"
                                      value={rule.condition.operator}
                                      onChange={(e) =>
                                        updateRule(rule.id, (r) => ({
                                          ...r,
                                          condition: { ...r.condition, operator: e.target.value as SpecRuleOperator },
                                        }))
                                      }
                                    >
                                      {condOps.map((op) => (
                                        <option key={op.value} value={op.value}>
                                          {op.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="col-12">
                                    <RuleValueEditor
                                      constraint={condConstraint}
                                      operator={rule.condition.operator}
                                      value={rule.condition.value ?? ''}
                                      values={rule.condition.values ?? []}
                                      onChangeValue={(v) =>
                                        updateRule(rule.id, (r) => ({ ...r, condition: { ...r.condition, value: v } }))
                                      }
                                      onChangeValues={(v) =>
                                        updateRule(rule.id, (r) => ({ ...r, condition: { ...r.condition, values: v } }))
                                      }
                                    />
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="mb-2 small fw-semibold text-success">İSE</div>
                            <div className="row g-2 align-items-center">
                              <div className="col-12 col-sm-6">
                                <select
                                  className="form-select form-select-sm"
                                  value={rule.outcome.specKey}
                                  onChange={(e) =>
                                    updateRule(rule.id, (r) => ({
                                      ...r,
                                      outcome: { ...r.outcome, specKey: e.target.value, value: '', values: [] },
                                    }))
                                  }
                                >
                                  {constraints.map((c) => (
                                    <option key={c.id} value={c.key}>
                                      {c.displayName}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12 col-sm-6">
                                <select
                                  className="form-select form-select-sm"
                                  value={rule.outcome.operator}
                                  onChange={(e) =>
                                    updateRule(rule.id, (r) => ({
                                      ...r,
                                      outcome: { ...r.outcome, operator: e.target.value as SpecRuleOperator },
                                    }))
                                  }
                                >
                                  {outcomeOps.map((op) => (
                                    <option key={op.value} value={op.value}>
                                      {op.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12">
                                <RuleValueEditor
                                  constraint={outcomeConstraint}
                                  operator={rule.outcome.operator}
                                  value={rule.outcome.value ?? ''}
                                  values={rule.outcome.values ?? []}
                                  onChangeValue={(v) =>
                                    updateRule(rule.id, (r) => ({ ...r, outcome: { ...r.outcome, value: v } }))
                                  }
                                  onChangeValues={(v) =>
                                    updateRule(rule.id, (r) => ({ ...r, outcome: { ...r.outcome, values: v } }))
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div>
                        <Button type="button" className="btn btn-outline-primary btn-sm" onClick={addRule}>
                          <i className="fas fa-plus me-1" />
                          Kural Ekle
                        </Button>
                      </div>
                    </div>
                  )}

                  {tab === 'ordering' && (
                    <div className="d-flex flex-column gap-3">
                      {enumConstraints.length === 0 && (
                        <p className="text-muted small mb-0">Sıralanacak seçenek listesi (Enum) tipinde özellik yok.</p>
                      )}
                      {enumConstraints.map((c) => (
                        <div key={c.id} className="border rounded p-3">
                          <div className="small fw-semibold mb-2">{c.displayName}</div>
                          <ul className="list-group">
                            {(orders[c.id] ?? []).map((value, index) => (
                              <li
                                key={value}
                                className="list-group-item d-flex align-items-center gap-2"
                                draggable
                                onDragStart={() => setDragKey(`${c.id}:${index}`)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                  if (!dragKey) return;
                                  const [sid, fromStr] = dragKey.split(':');
                                  if (Number(sid) === c.id) reorder(c.id, Number(fromStr), index);
                                  setDragKey(null);
                                }}
                                style={{ cursor: 'grab' }}
                              >
                                <i className="fas fa-grip-vertical text-muted" aria-hidden="true" />
                                <span>{value}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {error && <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>}
              {success && <div className="alert alert-success py-2 small mt-3 mb-0">{success}</div>}
            </div>
            <div className="modal-footer">
              <Button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Kapat
              </Button>
              <Button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || selectedId == null || loading}
              >
                {saving ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}
